// Mobile parity: 10-digit phone (digits only) + country code picker + optional
// LinkedIn URL. Country code list is identical to the mobile app.
import { useRef, useState } from "react"
import { useOnboarding } from "../../contexts/OnboardingContext"
import {
  OnboardingStepShell,
  ContinueButton,
  OB_COLORS as C,
  OB_FF as FF,
  BARE_INPUT_STYLE,
} from "./OnboardingStepShell"

const CODES = [
  { flag: "🇮🇳", code: "+91",  name: "India" },
  { flag: "🇺🇸", code: "+1",   name: "United States" },
  { flag: "🇬🇧", code: "+44",  name: "United Kingdom" },
  { flag: "🇦🇺", code: "+61",  name: "Australia" },
  { flag: "🇨🇦", code: "+1",   name: "Canada" },
  { flag: "🇸🇬", code: "+65",  name: "Singapore" },
  { flag: "🇦🇪", code: "+971", name: "UAE" },
  { flag: "🇩🇪", code: "+49",  name: "Germany" },
  { flag: "🇫🇷", code: "+33",  name: "France" },
  { flag: "🇳🇱", code: "+31",  name: "Netherlands" },
  { flag: "🇮🇪", code: "+353", name: "Ireland" },
  { flag: "🇸🇪", code: "+46",  name: "Sweden" },
  { flag: "🇳🇿", code: "+64",  name: "New Zealand" },
  { flag: "🇯🇵", code: "+81",  name: "Japan" },
  { flag: "🇰🇷", code: "+82",  name: "South Korea" },
  { flag: "🇧🇷", code: "+55",  name: "Brazil" },
  { flag: "🇿🇦", code: "+27",  name: "South Africa" },
  { flag: "🇲🇾", code: "+60",  name: "Malaysia" },
  { flag: "🇵🇭", code: "+63",  name: "Philippines" },
  { flag: "🇵🇰", code: "+92",  name: "Pakistan" },
]

const flagFor = (code) => (CODES.find((c) => c.code === code)?.flag) || "🌍"

export default function ContactDetailsStep({ onContinue, onBack, step, totalSteps }) {
  const { profile, updateField } = useOnboarding()
  const [phone, setPhone]       = useState(profile?.phone || "")
  const [code,  setCode]        = useState(profile?.countryCode || "+91")
  const [linkedin, setLinkedin] = useState(profile?.linkedinUrl || "")
  const [pickerOpen, setPickerOpen] = useState(false)
  const sheetRef = useRef(null)

  const canContinue = phone.trim().length === 10

  const flag = flagFor(code)

  const submit = () => {
    if (!canContinue) return
    const trimmed = linkedin.trim()
    updateField({
      phone: phone.trim(),
      countryCode: code,
      linkedinUrl: trimmed || undefined,
    })
    onContinue()
  }

  return (
    <OnboardingStepShell
      title={"How can employers\nreach you?"}
      subtitle={"Used to fill contact fields automatically."}
      step={step}
      totalSteps={totalSteps}
      onBack={onBack}
      bottom={<ContinueButton label="Continue" onClick={submit} disabled={!canContinue} />}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {/* PHONE */}
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, color: C.textMuted,
            letterSpacing: "0.1em", marginBottom: 8,
          }}>
            PHONE
          </div>
          <div style={{
            display: "flex", alignItems: "center",
            minHeight: 56,
            padding: "0 16px",
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 22, gap: 12,
          }}>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "none", border: "none", padding: 0,
                cursor: "pointer", fontFamily: FF, color: C.text,
              }}
            >
              <span style={{ fontSize: 18 }}>{flag}</span>
              <span style={{ fontSize: 15, fontWeight: 600 }}>{code}</span>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                <path d="M5 7l5 5 5-5" stroke={C.textMuted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div style={{ width: 1, height: 20, background: C.border, flexShrink: 0 }} />
            <input
              type="tel"
              inputMode="numeric"
              placeholder="9876543210"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              style={{ ...BARE_INPUT_STYLE, fontSize: 15 }}
            />
          </div>
        </div>

        {/* LINKEDIN */}
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, color: C.textMuted,
            letterSpacing: "0.1em", marginBottom: 8,
          }}>
            LINKEDIN
          </div>
          <div style={{
            display: "flex", alignItems: "center",
            minHeight: 56,
            padding: "0 16px",
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 22, gap: 12,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 6,
              background: "#0A66C2", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 800, flexShrink: 0,
            }}>in</div>
            <input
              type="url"
              placeholder="https://linkedin.com/in/username"
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              style={{ ...BARE_INPUT_STYLE, fontSize: 14 }}
            />
          </div>
        </div>
      </div>

      {/* Country-code sheet */}
      {pickerOpen && (
        <div
          onClick={() => setPickerOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(24,24,27,0.35)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
        >
          <div
            ref={sheetRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 480,
              maxHeight: "70vh",
              background: C.card, borderTopLeftRadius: 20, borderTopRightRadius: 20,
              display: "flex", flexDirection: "column",
              fontFamily: FF,
              overflow: "hidden",
            }}
          >
            <div style={{
              padding: "12px 0 10px",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
              borderBottom: `1px solid ${C.border}`,
            }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Select Country Code</div>
            </div>
            <div style={{ overflow: "auto" }}>
              {CODES.map((c, i) => {
                const sel = c.code === code
                return (
                  <button
                    key={`${c.code}-${c.name}`}
                    type="button"
                    onClick={() => { setCode(c.code); setPickerOpen(false) }}
                    style={{
                      width: "100%",
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "14px 16px",
                      background: "transparent", border: "none",
                      borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
                      cursor: "pointer", fontFamily: FF,
                      textAlign: "left",
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{c.flag}</span>
                    <span style={{
                      flex: 1,
                      fontSize: 14,
                      color: sel ? C.text : C.text,
                      fontWeight: sel ? 600 : 400,
                    }}>
                      {c.name}  <span style={{ color: C.textMuted }}>{c.code}</span>
                    </span>
                    {sel && (
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="8" fill={C.text} />
                        <path d="M6.5 10l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </OnboardingStepShell>
  )
}
